import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Box, Divider } from '@mui/material';
import { useMountedState } from 'react-use';

import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import ForecastGraphsSunlightTable from 'components/ForecastGraphsSunlightTable';
import useCustomError from 'hooks/useCustomError';
import type { SunlightTimes } from 'types/sunlightTimes';

import styles from './ForecastGraphsSunlightContainer.module.scss';

export interface ForecastGraphsSunlightContainerProps {
  daysToDisplay: Array<number>;
  sunlightTimes: SunlightTimes;
}

const ForecastGraphsSunlight = ({
  daysToDisplay,
  sunlightTimes,
}: ForecastGraphsSunlightContainerProps) => {
  const isMounted = useMountedState();

  const items = useMemo(
    () =>
      isMounted() &&
      daysToDisplay?.map((day: number, dayIndex) => (
        <Box key={day} className={styles.dayWrapper}>
          <Divider
            className={classNames({
              [styles.divider]: true,
              [styles.dividerVisible]: dayIndex !== 0,
            })}
            orientation="vertical"
            flexItem
          />
          <div className={styles.table}>
            <ForecastGraphsSunlightTable day={day} isColumn={false} sunlightTimes={sunlightTimes} />
          </div>
        </Box>
      )),
    [daysToDisplay, sunlightTimes, isMounted],
  );

  return (
    <div className={styles.wrapper}>
      <div className={classNames([styles.content], 'sl-section-full-bleed')}>{items}</div>
    </div>
  );
};

const ForecastGraphsSunlightContainer = ({
  daysToDisplay,
  sunlightTimes,
}: ForecastGraphsSunlightContainerProps) => {
  const { errorMessage, ErrorIcon, hasError } = useCustomError({ error: sunlightTimes?.error });

  if (!daysToDisplay || (!sunlightTimes.loading && !sunlightTimes.days)) return null;

  return (
    <div data-testid="sunlight-times">
      <ErrorBoundary
        error={hasError}
        errorBoundaryId="forecast-graphs-sunlight-container"
        render={() => (
          <Box className={styles.error} data-testid="sunlight-times-error">
            <ErrorMessage Icon={ErrorIcon} message={errorMessage} />
          </Box>
        )}
      >
        <ForecastGraphsSunlight daysToDisplay={daysToDisplay} sunlightTimes={sunlightTimes} />
      </ErrorBoundary>
    </div>
  );
};
export default React.memo(ForecastGraphsSunlightContainer);
